import { Container, Grid, Typography, Box, Card, CardContent, Chip, Button, Avatar } from "@mui/material";
import styled, { keyframes } from "styled-components";
import InnerBanner from "../presentation/InnerBanner";
import SEO from "../global/SEO";
import SchoolIcon from "@mui/icons-material/School";
import AssignmentIcon from "@mui/icons-material/Assignment";
import BookIcon from "@mui/icons-material/Book";
import PublicIcon from "@mui/icons-material/Public";
import VerifiedIcon from "@mui/icons-material/Verified";
import SupportIcon from "@mui/icons-material/Support";
import ScheduleIcon from "@mui/icons-material/Schedule";
import SecurityIcon from "@mui/icons-material/Security";
import StarIcon from "@mui/icons-material/Star";
import LocationOnIcon from "@mui/icons-material/LocationOn";

const AustraliaAssignmentHelp = () => {
  const services = [
    {
      title: "Essay Help Australia",
      description: "Professional essay writing services tailored to Australian university standards and requirements.",
      features: ["Australian academic standards", "Proper referencing", "On-time delivery", "Free revisions"],
      icon: <AssignmentIcon />,
      color: "#21a5a7",
      gradient: "linear-gradient(135deg, #21a5a7 0%, #1f2a62 100%)"
    },
    {
      title: "Coursework Help Australia",
      description: "Reliable coursework assistance for Australian students with university-specific guidance.",
      features: ["University guidelines", "Subject expertise", "Plagiarism-free", "24/7 support"],
      icon: <BookIcon />,
      color: "#1f2a62",
      gradient: "linear-gradient(135deg, #1f2a62 0%, #21a5a7 100%)"
    },
    {
      title: "Dissertation Help Australia",
      description: "Expert dissertation support for Australian universities with comprehensive research.",
      features: ["Australian research standards", "Original content", "Proper formatting", "Chapter delivery"],
      icon: <SchoolIcon />,
      color: "#21a5a7",
      gradient: "linear-gradient(135deg, #21a5a7 0%, #1f2a62 100%)"
    },
    {
      title: "Assignment Help for Indian Students in Australia",
      description: "Specialized support for Indian students studying in Australian universities.",
      features: ["Cultural understanding", "Local standards", "WhatsApp support", "Affordable pricing"],
      icon: <PublicIcon />,
      color: "#1f2a62",
      gradient: "linear-gradient(135deg, #1f2a62 0%, #21a5a7 100%)"
    }
  ];

  const features = [
    {
      title: "Australian Standards",
      description: "All assignments follow Australian university guidelines and academic standards",
      icon: <VerifiedIcon />,
      color: "#21a5a7"
    },
    {
      title: "24/7 Support",
      description: "Available round the clock via WhatsApp, chat, and email for urgent queries",
      icon: <SupportIcon />,
      color: "#1f2a62"
    },
    {
      title: "Affordable Pricing",
      description: "Competitive rates designed for students with transparent pricing",
      icon: <StarIcon />,
      color: "#21a5a7"
    }
  ];

  const stats = [
    { number: "500+", label: "Happy Students" },
    { number: "98%", label: "Success Rate" },
    { number: "24/7", label: "Support Available" },
    { number: "100%", label: "Original Content" }
  ];

  return (
    <>
      <SEO 
        title="Assignment Help in Australia | Instant Academic Support for International Students"
        description="LiveAssignment offers expert academic help to students in Australia, UK, Canada, and New Zealand. Trusted by Indian students abroad. Affordable & fast service."
        keywords="assignment help Australia, Australian assignment help, essay help Australia, coursework help Australia, Indian students Australia, academic help Australia, university assignment help"
        h1Heading="Get Online Assignment Help in Australia, UK, Canada & More"
        canonicalUrl="https://liveassignment.com/australia-assignment-help"
      />
      <InnerBanner pageTitle="Assignment Help in Australia"></InnerBanner>
      <Wrapper>
        <Container>
          <HeaderSection>
            <Typography variant="h2" component="h1" align="center" gutterBottom>
              Get Online Assignment Help in Australia, UK, Canada & More
            </Typography>
            <Typography variant="h6" align="center" color="textSecondary" paragraph>
              Trusted academic support for international students studying in Australia and beyond
            </Typography>
          </HeaderSection>

          <StatsSection>
            <Grid container spacing={3}>
              {stats.map((stat, index) => (
                <Grid item xs={6} sm={3} key={index}>
                  <StatCard>
                    <Typography variant="h3" color="primary" gutterBottom>
                      {stat.number}
                    </Typography>
                    <Typography variant="body2" align="center">
                      {stat.label}
                    </Typography>
                  </StatCard>
                </Grid>
              ))}
            </Grid>
          </StatsSection>
          
          <ServicesSection>
            <Typography variant="h4" align="center" gutterBottom>
              Our Australian Assignment Services
            </Typography>
            <Grid container spacing={3}>
              {services.map((service, index) => (
                <Grid item xs={12} sm={6} lg={3} key={index}>
                  <ServiceCard className="service-card">
                    <CardHeader>
                      <IconWrapper gradient={service.gradient}>
                        {service.icon}
                      </IconWrapper>
                      <Typography variant="h6" component="h3" gutterBottom color="primary">
                        {service.title}
                      </Typography>
                    </CardHeader>
                    <CardContent>
                      <Typography variant="body2" paragraph>
                        {service.description}
                      </Typography>
                      <FeaturesList>
                        {service.features.map((feature, idx) => (
                          <Chip 
                            key={idx} 
                            label={feature} 
                            size="small" 
                            color="primary" 
                            variant="outlined"
                            sx={{ m: 0.5 }}
                          />
                        ))}
                      </FeaturesList>
                      <Button 
                        variant="contained" 
                        color="primary" 
                        fullWidth 
                        sx={{ mt: 2 }}
                      >
                        Get Started
                      </Button>
                    </CardContent>
                  </ServiceCard>
                </Grid>
              ))}
            </Grid>
          </ServicesSection>
          
          <FeaturesSection>
            <Typography variant="h4" align="center" gutterBottom>
              Why Choose Our Australia Assignment Help?
            </Typography>
            <Grid container spacing={4}>
              {features.map((feature, index) => (
                <Grid item xs={12} sm={6} lg={4} key={index}>
                  <FeatureCard className="feature-card">
                    <FeatureIcon color={feature.color}>
                      {feature.icon}
                    </FeatureIcon>
                    <Typography variant="h6" color="primary" gutterBottom>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" align="center">
                      {feature.description}
                    </Typography>
                  </FeatureCard>
                </Grid>
              ))}
            </Grid>
          </FeaturesSection>
          
          <TrustSection>
            <Typography variant="h4" gutterBottom align="center">
              Trusted by Indian Students in Australia
            </Typography>
            <Typography variant="body1" paragraph align="center">
              We understand the unique challenges faced by Indian students studying abroad. 
              Our team provides culturally sensitive support while maintaining the highest academic standards.
            </Typography>
            <LocationInfo>
              <LocationOnIcon color="primary" />
              <Typography variant="body2" color="primary">
                Serving students across Australia, UK, Canada & New Zealand
              </Typography>
            </LocationInfo>
          </TrustSection>
        </Container>
      </Wrapper>
    </>
  );
};

export default AustraliaAssignmentHelp;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
`;

const Wrapper = styled.div`
  padding: 70px 0;
  position: relative;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);

  @media (max-width: 992px) {
    padding: 40px 0;
  }
`;

const HeaderSection = styled.div`
  text-align: center;
  margin-bottom: 60px;
  
  .MuiTypography-h2 {
    background: linear-gradient(135deg, #1f2a62 0%, #21a5a7 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    font-weight: 700;
    
    @media (max-width: 768px) {
      font-size: 2rem;
    }
  }
`;

const StatsSection = styled.div`
  margin-bottom: 80px;
  
  .MuiGrid-item {
    display: flex;
  }
`;

const StatCard = styled(Box)`
  text-align: center;
  padding: 30px 20px;
  background: white;
  border-radius: 15px;
  box-shadow: 0 5px 20px rgba(0,0,0,0.1);
  transition: all 0.3s ease;
  height: 100%;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 30px rgba(0,0,0,0.15);
  }
  
  .MuiTypography-h3 {
    font-weight: 700;
    background: linear-gradient(135deg, #1f2a62 0%, #21a5a7 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const ServicesSection = styled.div`
  margin-bottom: 80px;
  
  .MuiTypography-h4 {
    margin-bottom: 40px;
    background: linear-gradient(135deg, #1f2a62 0%, #21a5a7 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    font-weight: 700;
  }
  
  .MuiGrid-item {
    display: flex;
    margin-bottom: 20px;
  }
`;

const ServiceCard = styled(Card)`
  height: 100%;
  border-radius: 20px;
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  border: none;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
  display: flex;
  flex-direction: column;
  
  &:hover {
    transform: translateY(-10px) scale(1.02);
    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
  }

  .MuiCardContent-root {
    padding: 30px;
    flex-grow: 1;
    display: flex;
    flex-direction: column;
  }

  .MuiTypography-h6 {
    font-weight: 600;
    margin-bottom: 15px;
    text-align: center;
  }

  .MuiChip-root {
    font-size: 12px;
    height: 24px;
  }
`;

const CardHeader = styled.div`
  text-align: center;
  padding: 30px 30px 0;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
`;

const IconWrapper = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  background: ${props => props.gradient};
  color: white;
  font-size: 40px;
  animation: ${pulse} 2s infinite;
  
  .MuiSvgIcon-root {
    font-size: 40px;
  }
`;

const FeaturesList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 15px 0;
`;

const FeaturesSection = styled.div`
  text-align: center;
  padding: 60px 0;
  
  .MuiTypography-h4 {
    margin-bottom: 40px;
    background: linear-gradient(135deg, #1f2a62 0%, #21a5a7 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    font-weight: 700;
  }
  
  .MuiGrid-item {
    display: flex;
    margin-bottom: 20px;
  }
`;

const FeatureCard = styled(Box)`
  text-align: center;
  padding: 40px 20px;
  border-radius: 20px;
  background: white;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
  transition: all 0.3s ease;
  height: 100%;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 35px rgba(0,0,0,0.15);
  }
  
  .MuiTypography-h6 {
    margin: 20px 0 15px;
    font-weight: 600;
  }
`;

const FeatureIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  background: ${props => props.color};
  color: white;
  font-size: 30px;
  
  .MuiSvgIcon-root {
    font-size: 30px;
  }
`;

const TrustSection = styled.div`
  text-align: center;
  padding: 60px 40px;
  background: white;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
  margin-top: 60px;
  
  .MuiTypography-h4 {
    background: linear-gradient(135deg, #1f2a62 0%, #21a5a7 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    font-weight: 700;
    margin-bottom: 20px;
  }
`;

const LocationInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 20px;
  
  .MuiSvgIcon-root {
    font-size: 24px;
  }
`; 